import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Stack, Typography, Link, Divider } from '@mui/material';
import { Phone, Email, LocationOn } from '@mui/icons-material';

type ContactEntry = {
    key: 'minsk' | 'warszawa' | 'piaseczno';
    title: string;
    phone: { text: string; href: string };
    email: { text: string; href: string };
    addr1: string;
    addr2: string;
    mapHref: string;
};

const contacts: ContactEntry[] = [
    {
        key: 'minsk',
        title: 'Mińsk Mazowiecki',
        phone: { text: '602-631-217', href: 'tel:602631217' },
        email: { text: 'biuro@ufo-meble.pl', href: 'mailto:biuro@ufo-meble.pl' },
        addr1: '05-300 Mińsk Mazowiecki',
        addr2: 'Stojadła, ul. Warszawska 93/95',
        mapHref: 'https://goo.gl/maps/A4B7CHgHQ2oozQR69',
    },
    {
        key: 'warszawa',
        title: 'Warszawa',
        phone: { text: '604-485-537', href: 'tel:604485537' },
        email: { text: 'biuro1@ufo-meble.pl', href: 'mailto:biuro1@ufo-meble.pl' },
        addr1: '04-148 Warszawa',
        addr2: 'ul. Grochowska 129',
        mapHref: 'https://www.google.com/maps/search/?api=1&query=Ul.+Grochowska+129,+04-148+Warszawa',
    },
    {
        key: 'piaseczno',
        title: 'Piaseczno',
        phone: { text: '506-003-090', href: 'tel:506003090' },
        email: { text: 'ufopiaseczno@wp.pl', href: 'mailto:ufopiaseczno@wp.pl' },
        addr1: '05-500 Piaseczno',
        addr2: 'plac Piłsudskiego 6',
        mapHref: 'https://maps.app.goo.gl/b6zTLxwxDuSyjMsK8',
    },
];

const footerGradient =
    'linear-gradient(180deg, #2f3ea4 0%, #293a98 40%, #26358f 70%, #223083 100%)';

const accents = ['#2f3ea4', '#dc2430', '#414345'] as const;

const MinimalContacts: FC = () => (
    <Box
        sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            overflowX: 'hidden',
        }}
    >
        <Box
            sx={{
                width: '100%',
                mx: 'auto',
                maxWidth: 1300,
                px: { xs: 2, md: 3 },
                py: { xs: 3, md: 4 },
                boxSizing: 'border-box',
                overflowX: 'hidden',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', md: 'space-around' },
                    alignItems: 'stretch',
                    columnGap: { xs: 2, md: 3 },
                    rowGap: { xs: 2.5, md: 3 },
                }}
            >
                {contacts.map((c, idx) => (
                    <Box
                        key={c.key}
                        sx={{
                            flex: { xs: '1 1 100%', sm: '0 1 360px', md: '0 0 360px' },
                            width: { xs: '100%', md: 'auto' },
                            minWidth: 0,
                            maxWidth: 420,
                            px: { xs: 1, md: 2 },
                            py: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            borderLeft: '4px solid',
                            borderColor: accents[idx] || accents[0],
                            boxSizing: 'border-box',
                        }}
                    >
                        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '.16em' }}>
                            Salon
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.15, mb: 0.5 }}
                        >
                            {c.title}
                        </Typography>

                        <Stack spacing={1.1} sx={{ mt: 0.5 }}>
                            <Stack direction="row" spacing={1.1} alignItems="center">
                                <Phone sx={{ color: 'text.secondary' }} />
                                <Link href={c.phone.href} underline="hover" color="inherit">
                                    {c.phone.text}
                                </Link>
                            </Stack>

                            <Stack direction="row" spacing={1.1} alignItems="center">
                                <Email sx={{ color: 'text.secondary' }} />
                                <Link href={c.email.href} underline="hover" color="inherit">
                                    {c.email.text}
                                </Link>
                            </Stack>

                            <Stack direction="row" spacing={1.1} alignItems="flex-start">
                                <LocationOn sx={{ color: 'text.secondary', mt: '2px' }} />
                                <Box>
                                    <Link
                                        href={c.mapHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                        color="inherit"
                                    >
                                        {c.addr1}
                                        <br />
                                        {c.addr2}
                                    </Link>
                                </Box>
                            </Stack>
                        </Stack>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const Footer: FC = () => {
    const { pathname } = useLocation();
    const year = new Date().getFullYear();

    return (
        <Box
            component="footer"
            sx={{
                width: '100%',
                overflowX: 'hidden',
                borderTop: '1px solid',
                borderColor: 'divider',
                mt: 12,
            }}
        >
            {pathname !== '/kontakt' && <MinimalContacts />}

            <Divider />

            <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        textAlign: 'center',
                        maxWidth: 1100,
                        mx: 'auto',
                        lineHeight: 1.6,
                        wordBreak: 'break-word',
                    }}
                >
                    Przedstawiona oferta nie stanowi oferty w rozumieniu Kodeksu Cywilnego, jest jedynie zaproszeniem do
                    rozpoczęcia rokowań (zgodnie z art. 71 k.c.). Z uwagi na różnorodność monitorów oraz kart graficznych z
                    których klienci korzystają, wyświetlane kolory mogą różnić się od rzeczywistych.
                </Typography>
            </Box>

            <Box
                sx={{
                    backgroundImage: footerGradient,
                    color: '#fff',
                    py: 2,
                    px: 2,
                    textAlign: 'center',
                    fontSize: 14,
                    maxWidth: '100vw',
                    mx: 'auto',
                    overflowX: 'hidden',
                }}
            >
                © UFO Spółka z ograniczoną odpowiedzialnością {year} |{' '}
                <Link
                    href="mailto:DAMIAM.G.PG@GMAIL.COM"
                    underline="hover"
                    sx={{ color: '#fff', opacity: 0.92, '&:hover': { opacity: 1 }, wordBreak: 'break-word' }}
                >
                    Realizacja: Damian Gorzkowski
                </Link>
            </Box>
        </Box>
    );
};

export default Footer;